import {type UseMutateFunction, useMutation} from "@tanstack/react-query";
import {useAuthStore} from "@/store/auth.store";
import {decodeJwt} from "@/util/jwt";
import {getMetaPixelParams} from "@/util/metaPixel";
import {registrationService} from "./registration.service";
import type {RegistrationRequest, RegistrationResult} from "./registration.types";

export interface RegisterInput {
  email: string;
  password: string;
  birthDate: string;
  preferredBonus: RegistrationRequest["preferredBonus"];
}

export interface UseRegistrationResult {
  register: UseMutateFunction<RegistrationResult, Error, RegisterInput>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
  reset: () => void;
}

export const useRegistration = (): UseRegistrationResult => {
  const setSession = useAuthStore(state => state.setSession);

  const mutation = useMutation<RegistrationResult, Error, RegisterInput>({
    mutationKey: ["auth", "register"],
    mutationFn: input => {
      const {fbp, fbc} = getMetaPixelParams();
      const payload: RegistrationRequest = {...input};
      if (fbp) payload.fbp = fbp;
      if (fbc) payload.fbc = fbc;
      return registrationService(payload);
    },
    onSuccess: result => {
      const tokenPayload = decodeJwt(result.accessToken);
      setSession({token: result.accessToken, user: tokenPayload ?? {}});
    },
  });

  return {
    register: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
};
